import React, { useState, ChangeEvent, useEffect } from 'react';
import { parsePhoneNumber, isValidPhoneNumber } from 'react-phone-number-input/mobile';
import PhoneInput from 'react-phone-number-input/mobile';
import { observer } from 'mobx-react-lite';

import 'react-phone-number-input/style.css';

import { useMst } from '../../../models/rootModel';
import { SignUpData } from '../../../models/types/UserDiscoveryResponse.types';

import { useAnalytics } from '../../../utils/analytics';
import { sendGTMEvent } from '../../../utils/googleTagManager';
import { capitalizeFirstLetter, isMobileAppWebView } from '../../../utils/utilsFunctions';
import { goBackToMobileApp } from '../../../utils/mobileApp';
import { ActionName } from '../../../utils/mobileApp/mobileApp.types';

import { BaseStep } from '../../common/BaseStep';
import { InputField } from '../../common/InputField/InputField';
import { TermsOfServicePopUp } from '../../common/TermsOfServicePopUp';
import { SignUpFields, SignUpStepProps, ErrorCode, SignUpValues, ServerError } from './SignUp.types';
import { SignUpErrorPopUp } from './SignUpErrorPopUp';

import { SignUpBottomTextInput } from '../../styledComponent/Text.style';
import { StepWrapperBox } from '../../styledComponent/Boxes.style';
import { InputRoot, Label, ErrorInputText, TermsLabel } from '../../styledComponent/SignUpComponents.style';
import { SpanLink } from '../../styledComponent/SignUpComponents.style';

export const SignUpStep = observer((props: SignUpStepProps) => {
  const { inputFields, nextStep, isActive, doNotFindGroupsOptions, stepType } = props;
  const { UserDiscoveryResponse, Step, UserPayment, Group } = useMst();
  const [isValidInputs, setIsValidInputs] = useState(false);
  const [isPopUpOpen, setIsPopUpOpen] = useState(false);
  const [serverErrorMessage, setServerErrorMessage] = useState<ServerError>();
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState(true);
  const [isTermsPopUpOpen, setIsTermsPopUpOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const analytics = useAnalytics();

  useEffect(() => {
    if (isActive) {
      analytics.track('view signup step', { referringUrl: document.referrer });
      window.scrollTo(0, 0);
    }
  }, [isActive, analytics]);

  const [signUpValues, setSignUpValues] = useState<SignUpValues>({
    [SignUpFields.Name]: '',
    [SignUpFields.Phone]: '',
    [SignUpFields.Email]: '',
  });

  const [signUpInputError, setSignUpInputError] = useState<SignUpValues>({
    [SignUpFields.Name]: '',
    [SignUpFields.Phone]: '',
    [SignUpFields.Email]: '',
  });

  useEffect(() => {
    const checkValidInputs =
      signUpValues[SignUpFields.Name].length > 0 &&
      signUpValues[SignUpFields.Email].length > 0 &&
      signUpValues[SignUpFields.Phone].length > 0;
    checkValidInputs ? setIsValidInputs(true) : setIsValidInputs(false);
  }, [signUpValues]);

  useEffect(() => {
    setSubmitButtonDisabled(!isValidInputs);
  }, [isValidInputs]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    // Doesn't allow to enter number or other illegal chars, allows space, letters and ' -
    if (name === 'name' && /([\d!@#$%^&*()_+={}"|\\/[\]`,.?])+/.test(value)) {
      return;
    }
    setSignUpInputError((prevValues) => ({
      ...prevValues,
      [name]: '',
    }));

    setSignUpValues((prevState) => {
      return { ...prevState, [name]: value };
    });
  };

  const phoneChange = (value: string | undefined) => {
    setSignUpInputError((prevValues) => ({
      ...prevValues,
      [SignUpFields.Phone]: '',
    }));
    if (typeof value === 'string') {
      setSignUpValues((prevState) => {
        return { ...prevState, [SignUpFields.Phone]: value };
      });
    }
  };

  const sendData = async () => {
    const dataToSend: SignUpData = UserDiscoveryResponse.signUpDateToSend;

    if (UserPayment.appliedCouponCode) {
      dataToSend.couponCode = UserPayment.appliedCouponCode;
    }

    try {
      const resSignUpUser = await UserDiscoveryResponse.signUpUser({ ...dataToSend, origin: Step.origin ?? '' });
      if (resSignUpUser.kind !== 'ok') {
        throw new Error('Welcome back! We found an existing account with your contact details.');
      }

      const { accountId } = resSignUpUser.data;

      sendGTMEvent({
        event: 'signup_completed',
        user_id: accountId,
        phone: dataToSend.phone,
        full_name: dataToSend.name,
        email: dataToSend.email,
      });

      analytics.track(`click signUp complete ${props.type}`, {
        stepId: props.id,
        stepType: props.type,
        stepIndex: props.stepIndex,
        stepTitle: props.title ?? '',
        ...UserDiscoveryResponse.utms,
        origin: Step.origin,
        userID: accountId,
      });

      if (!doNotFindGroupsOptions) Group.findGroupsOptions();

      if (isMobileAppWebView) {
        goBackToMobileApp({ accessToken: localStorage.getItem('authToken'), action: ActionName.Silent_Login });
      }

      if (props.totalSteps !== props.stepIndex + 1) {
        setTimeout(() => {
          setIsLoading(false);
          nextStep();
        }, 2000);
      }
    } catch (error) {
      if (error instanceof Error) {
        setServerErrorMessage({ message: error.message, errorCode: ErrorCode.Exists });
        setIsValidInputs(false);
        setIsPopUpOpen(true);
      }
      setIsLoading(false);
    } finally {
      setIsValidInputs(false);
    }
  };

  const onSubmit = async () => {
    const parsedPhone = parsePhoneNumber(signUpValues[SignUpFields.Phone]);
    if (parsedPhone) {
      const params = {
        phone: parsedPhone.nationalNumber,
        country_code: `+${parsedPhone.countryCallingCode}`,
        email: signUpValues[SignUpFields.Email],
      };
      const urlParams = new URLSearchParams(params);
      try {
        const resCanUserSignUp = await UserDiscoveryResponse.canUserSignUp(urlParams.toString());
        if (resCanUserSignUp.kind !== 'ok') {
          setIsLoading(false);
          throw new Error(resCanUserSignUp.data.error || ErrorCode.InternalServerError);
        }
        await sendData();
      } catch (error) {
        if (error instanceof Error) {
          const message =
            error.message === ErrorCode.Exists
              ? 'Welcome back! We found an existing account with your contact details. Please click on the button below to log back in.'
              : 'Sorry, something went wrong...';
          setServerErrorMessage({ message, errorCode: ErrorCode.Exists });
          setIsValidInputs(false);
          setIsPopUpOpen(true);
          setIsLoading(false);
        }
      }
    }
  };

  const validatedSignUpInformation = () => {
    setIsLoading(true);
    let isThereError = false;
    analytics.track('signup confirm click', { stepType });

    if (!isValidPhoneNumber(signUpValues[SignUpFields.Phone])) {
      isThereError = true;
      setSignUpInputError((prevValues) => ({
        ...prevValues,
        phone: 'Please enter your correct phone number',
      }));
    }

    if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(signUpValues[SignUpFields.Email])) {
      isThereError = true;
      setSignUpInputError((prevValues) => ({
        ...prevValues,
        email: 'Please enter a valid email. E.g. name@domain.com',
      }));
    }

    if (signUpValues[SignUpFields.Email] === 'name@domain.com') {
      isThereError = true;
      setSignUpInputError((prevValues) => ({
        ...prevValues,
        email: 'Use your real email :)',
      }));
    }

    if (isThereError) {
      setIsValidInputs(false);
      setIsLoading(false);
      return;
    }

    UserDiscoveryResponse.setUserNameAndEmail({
      email: signUpValues[SignUpFields.Email],
      name: signUpValues[SignUpFields.Name],
    });
    UserDiscoveryResponse.setUserPhoneNumber(signUpValues[SignUpFields.Phone]);

    setIsValidInputs(true);
    setSubmitButtonDisabled(true);
    onSubmit();
  };

  const onClosePopUp = () => {
    setIsPopUpOpen(false);
    if (serverErrorMessage?.errorCode === ErrorCode.Exists) {
      window.open(process.env.REACT_APP_API_LOGIN, '_self');
    }
  };

  const openTerms = () => {
    if (!isMobileAppWebView) {
      window.open(`${process.env.REACT_APP_WEBSITE}/terms-of-use/`, '_blank');
      return;
    }
  };

  const openPrivacy = () => {
    if (!isMobileAppWebView) {
      window.open(`${process.env.REACT_APP_WEBSITE}/privacy/`, '_blank');
      return;
    }
  };

  const idStart = 'signUpForm';

  return (
    <>
      {isActive ? (
        <BaseStep
          {...props}
          isButtonDisabled={submitButtonDisabled}
          changeButtonFunction={validatedSignUpInformation}
          isLoading={isLoading}
        >
          <TermsOfServicePopUp open={isTermsPopUpOpen} handleClose={() => setIsTermsPopUpOpen(false)} />
          <StepWrapperBox>
            {inputFields.map((inputField, index) => (
              <InputRoot key={`${inputField.type}-${index}`}>
                <Label>{inputField.label}</Label>
                {inputField.type !== SignUpFields.Phone ? (
                  <InputField
                    id={`${idStart}${capitalizeFirstLetter(inputField.type)}`}
                    type={inputField.type === SignUpFields.Name ? 'text' : 'email'}
                    error={signUpInputError[inputField.type]}
                    name={inputField.type}
                    maxLength={30}
                    handleChange={(event: ChangeEvent<HTMLInputElement>) => handleChange(event)}
                    value={signUpValues[inputField.type]}
                    placeholder={inputField.placeHolder}
                    bottomText={inputField.explanation ? inputField.explanation : ''}
                  />
                ) : (
                  <>
                    <PhoneInput
                      id={`${idStart}${capitalizeFirstLetter(inputField.type)}`}
                      onChange={(value: string | undefined) => phoneChange(value)}
                      value={signUpValues[SignUpFields.Phone]}
                      placeholder={inputField.placeHolder}
                      defaultCountry={'US'}
                      maxLength={16}
                    />
                    {inputField.explanation && signUpInputError[inputField.type].length === 0 ? (
                      <SignUpBottomTextInput>{inputField.explanation}</SignUpBottomTextInput>
                    ) : (
                      ''
                    )}
                  </>
                )}
                {signUpInputError[inputField.type].length > 0 ? (
                  <ErrorInputText>{signUpInputError[inputField.type]}</ErrorInputText>
                ) : (
                  ''
                )}
              </InputRoot>
            ))}
            <TermsLabel>
              By continuing, you agree to the Circles <SpanLink onClick={openTerms}>terms of use</SpanLink> and <SpanLink onClick={openPrivacy}>privacy policy</SpanLink>
            </TermsLabel>
          </StepWrapperBox>

          <SignUpErrorPopUp
            setIsPopUpOpen={setIsPopUpOpen}
            isPopUpOpen={isPopUpOpen}
            onClosePopUp={onClosePopUp}
            serverErrorMessage={serverErrorMessage}
          />
        </BaseStep>
      ) : (
        ''
      )}
    </>
  );
});
